/**
 * Modal de configuração para node de condição (estilo Typebot)
 * Permite múltiplos blocos IF com várias condições cada
 * ELSE é fixo e não precisa de configuração
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FlowNode, ConditionConfig } from '@/types/flows';
import { toast } from 'sonner';
import { HelpCircle, Plus, Trash2, GripVertical, Settings2 } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
// ScrollArea removido - usando scroll nativo para robustez (v1.1.0)
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ValueConfigInput, MatchOptions } from './ValueConfigInput';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

/**
 * Operadores disponíveis para condições
 */
/**
 * Operadores disponíveis para condições
 * Inclui operadores específicos para verificação de arrays
 */
const OPERATORS = [
  { value: 'equals', label: 'Igual a', needsValue: true },
  { value: 'not_equals', label: 'Diferente de', needsValue: true },
  { value: 'contains', label: 'Contém', needsValue: true },
  { value: 'not_contains', label: 'Não contém', needsValue: true },
  { value: 'starts_with', label: 'Começa com', needsValue: true },
  { value: 'ends_with', label: 'Termina com', needsValue: true },
  { value: 'greater', label: 'Maior que', needsValue: true },
  { value: 'less', label: 'Menor que', needsValue: true },
  { value: 'greater_equal', label: 'Maior ou igual', needsValue: true },
  { value: 'less_equal', label: 'Menor ou igual', needsValue: true },
  { value: 'is_set', label: 'Está definido', needsValue: false },
  { value: 'is_empty', label: 'Está vazio', needsValue: false },
  { value: 'has_items', label: 'Tem itens (array)', needsValue: false },
  { value: 'is_empty_array', label: 'Está vazio (array)', needsValue: false },
  { value: 'regex', label: 'Regex', needsValue: true },
];

interface Condition {
  id: string;
  variable: string;
  operator: string;
  value: string;
  values?: string[];
  matchOptions?: MatchOptions;
}

interface Comparison {
  id: string;
  conditions: Condition[];
  logicOperator: 'and' | 'or';
}

interface ConfigureConditionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: ConditionConfig) => void;
  tenantId: string;
}

/**
 * Gera ID único para comparisons/conditions
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Opções de correspondência padrão
 */
const DEFAULT_MATCH_OPTIONS: MatchOptions = {
  ignoreCase: true,
  ignoreAccents: true,
  trimSpaces: true,
};

/**
 * Componente de configuração de condição (estilo Typebot)
 */
export function ConfigureConditionDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave, 
  tenantId 
}: ConfigureConditionDialogProps) {
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  /**
   * Inicializa estado quando node mudar
   */
  useEffect(() => {
    if (node && open) {
      const config = node.data.config || {};
      
      // Novo formato com múltiplos IFs
      if (config.comparisons && Array.isArray(config.comparisons)) {
        setComparisons(config.comparisons);
      }
      // Formato legado: converte para novo formato
      else if (config.conditions && Array.isArray(config.conditions)) {
        setComparisons([{
          id: generateId(),
          conditions: config.conditions,
          logicOperator: config.logicOperator || 'and',
        }]);
      }
      // Formato legado mais antigo (variable, operator, value diretos)
      else if (config.variable) {
        setComparisons([{
          id: generateId(),
          conditions: [{
            id: generateId(),
            variable: config.variable,
            operator: config.operator || 'equals',
            value: config.value || '',
          }],
          logicOperator: 'and',
        }]);
      }
      // Novo node: inicia com um IF vazio
      else {
        setComparisons([{
          id: generateId(),
          conditions: [{
            id: generateId(),
            variable: '',
            operator: 'equals',
            value: '',
          }],
          logicOperator: 'and',
        }]);
      }
    }
  }, [node, open]);

  /**
   * Adiciona novo bloco IF
   */
  const addComparison = () => {
    setComparisons(prev => [...prev, {
      id: generateId(),
      conditions: [{
        id: generateId(),
        variable: '',
        operator: 'equals',
        value: '',
        values: [],
        matchOptions: { ...DEFAULT_MATCH_OPTIONS },
      }],
      logicOperator: 'and',
    }]);
  };

  /**
   * Remove bloco IF
   */
  const removeComparison = (comparisonId: string) => {
    if (comparisons.length <= 1) {
      toast.error('É necessário ter pelo menos um bloco IF');
      return;
    }
    setComparisons(prev => prev.filter(c => c.id !== comparisonId));
  };

  /**
   * Adiciona condição a um bloco IF
   */
  const addCondition = (comparisonId: string) => {
    setComparisons(prev => prev.map(comp => 
      comp.id === comparisonId 
        ? { ...comp, conditions: [...comp.conditions, {
            id: generateId(),
            variable: '',
            operator: 'equals',
            value: '',
            values: [],
            matchOptions: { ...DEFAULT_MATCH_OPTIONS },
          }]}
        : comp
    ));
  };

  /**
   * Remove condição de um bloco IF
   */
  const removeCondition = (comparisonId: string, conditionId: string) => {
    setComparisons(prev => prev.map(comp => {
      if (comp.id !== comparisonId) return comp;
      if (comp.conditions.length <= 1) {
        toast.error('É necessário ter pelo menos uma condição por IF');
        return comp;
      }
      return { ...comp, conditions: comp.conditions.filter(c => c.id !== conditionId) };
    }));
  };

  /**
   * Atualiza condição
   */
  const updateCondition = (
    comparisonId: string, 
    conditionId: string, 
    field: keyof Condition, 
    value: any
  ) => {
    setComparisons(prev => prev.map(comp => 
      comp.id === comparisonId 
        ? { ...comp, conditions: comp.conditions.map(cond => 
            cond.id === conditionId 
              ? { ...cond, [field]: value }
              : cond
          )}
        : comp
    ));
  };

  /**
   * Atualiza valores múltiplos de uma condição
   */
  const updateConditionValues = (comparisonId: string, conditionId: string, values: string[]) => {
    updateCondition(comparisonId, conditionId, 'values', values);
    // Também atualiza value com o primeiro valor para compatibilidade
    if (values.length > 0) {
      updateCondition(comparisonId, conditionId, 'value', values[0]);
    }
  };

  /**
   * Atualiza opções de correspondência de uma condição
   */
  const updateConditionMatchOptions = (comparisonId: string, conditionId: string, options: MatchOptions) => {
    updateCondition(comparisonId, conditionId, 'matchOptions', options);
  };

  /**
   * Atualiza operador lógico de um bloco IF
   */
  const updateLogicOperator = (comparisonId: string, value: 'and' | 'or') => {
    setComparisons(prev => prev.map(comp => 
      comp.id === comparisonId 
        ? { ...comp, logicOperator: value }
        : comp
    ));
  };

  /**
   * Valida e salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    // Validação: pelo menos um IF com pelo menos uma condição com variável
    const hasValidComparison = comparisons.some(comp => 
      comp.conditions.some(cond => cond.variable.trim() !== '')
    );

    if (!hasValidComparison) {
      toast.error('Configure pelo menos uma condição com variável');
      return;
    }

    // Opções de normalização padrão
    const DEFAULT_MATCH_OPTIONS = {
      ignoreCase: true,
      ignoreAccents: true,
      trimSpaces: true,
    };

    // Remove condições vazias e garante estrutura completa antes de salvar
    const cleanedComparisons = comparisons.map(comp => ({
      ...comp,
      conditions: comp.conditions
        .filter(cond => cond.variable.trim() !== '')
        .map(cond => ({
          ...cond,
          // Garante que values sempre exista
          values: (cond.values && cond.values.length > 0) ? cond.values : (cond.value ? [cond.value] : []),
          // Garante que matchOptions sempre exista com defaults
          matchOptions: cond.matchOptions || DEFAULT_MATCH_OPTIONS,
        })),
    })).filter(comp => comp.conditions.length > 0);

    if (cleanedComparisons.length === 0) {
      toast.error('Configure pelo menos uma condição com variável');
      return;
    }

    onSave(node.id, { comparisons: cleanedComparisons, nickname: nicknameRef.current?.getNickname() } as any);
    toast.success('Configuração salva com sucesso');
    onOpenChange(false);
  };

  /**
   * Verifica se operador precisa de valor
   */
  const operatorNeedsValue = (operator: string) => {
    const op = OPERATORS.find(o => o.value === operator);
    return op?.needsValue !== false;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* v1.1.0: Padrão robusto de scroll - altura fixa + scroll nativo */}
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Condição</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* Body com scroll nativo - compatível com wheel, trackpad e drag */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Campo ID do Node (somente leitura) */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* Lista de blocos IF */}
              {comparisons.map((comparison, compIndex) => (
                <div 
                  key={comparison.id} 
                  className="border rounded-lg p-4 space-y-3 bg-muted/20"
                >
                  {/* Cabeçalho do IF */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      <Badge variant="outline" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-300">
                        IF #{compIndex + 1}
                      </Badge>
                    </div>
                    {comparisons.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeComparison(comparison.id)}
                        className="h-7 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Lista de condições do IF */}
                  {comparison.conditions.map((condition, condIndex) => (
                    <div key={condition.id} className="space-y-2">
                      {/* Separador com operador lógico (E/OU) se não for primeira condição */}
                      {condIndex > 0 && (
                        <div className="flex items-center gap-2 py-1">
                          <Separator className="flex-1" />
                          <Select 
                            value={comparison.logicOperator} 
                            onValueChange={(v) => updateLogicOperator(comparison.id, v as 'and' | 'or')}
                          >
                            <SelectTrigger className="w-16 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="and">E</SelectItem>
                              <SelectItem value="or">OU</SelectItem>
                            </SelectContent>
                          </Select>
                          <Separator className="flex-1" />
                        </div>
                      )}

                      {/* Linha da condição */}
                      <div className="flex items-start gap-2">
                        {/* Variável */}
                        <div className="flex-1 min-w-0">
                          <VariablePickerCombobox
                            tenantId={tenantId}
                            value=""
                            onChange={(selectedVar) => {
                              updateCondition(comparison.id, condition.id, 'variable', selectedVar.name);
                            }}
                            placeholder={condition.variable || "Variável"}
                          />
                        </div>

                        {/* Operador */}
                        <div className="w-36">
                          <Select 
                            value={condition.operator} 
                            onValueChange={(v) => updateCondition(comparison.id, condition.id, 'operator', v)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {OPERATORS.map(op => (
                                <SelectItem key={op.value} value={op.value}>
                                  {op.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Botão remover condição */}
                        {comparison.conditions.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeCondition(comparison.id, condition.id)}
                            className="h-9 px-2 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      {/* Configuração de valores (se operador precisar) */}
                      {operatorNeedsValue(condition.operator) && (
                        <Collapsible defaultOpen={true} className="mt-2">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2">
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Settings2 className="h-3 w-3" />
                                Valores: {(condition.values?.length || 0) > 0 
                                  ? condition.values?.slice(0, 3).join(', ') + (condition.values!.length > 3 ? '...' : '')
                                  : condition.value || 'Configure...'}
                              </span>
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 p-3 bg-background rounded-md border">
                            <ValueConfigInput
                              values={condition.values || (condition.value ? [condition.value] : [])}
                              matchOptions={condition.matchOptions || DEFAULT_MATCH_OPTIONS}
                              onValuesChange={(values) => updateConditionValues(comparison.id, condition.id, values)}
                              onMatchOptionsChange={(options) => updateConditionMatchOptions(comparison.id, condition.id, options)}
                              placeholder="Digite um valor aceito e pressione Enter"
                              tenantId={tenantId}
                              allowVariables={true}
                            />
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  ))}

                  {/* Botão adicionar condição */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => addCondition(comparison.id)}
                    className="w-full mt-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar condição
                  </Button>
                </div>
              ))}

              {/* Botão adicionar novo IF */}
              <Button 
                variant="outline" 
                onClick={addComparison}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-1" />
                Adicionar bloco IF
              </Button>

              {/* Bloco ELSE (informativo, não configurável) */}
              <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300">
                    ELSE
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Executado se nenhum IF for verdadeiro
                  </span>
                </div>
              </div>

              {/* Preview/Resumo */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Resumo da Condição:</p>
                <div className="text-xs space-y-1">
                  {comparisons.map((comp, i) => (
                    <div key={comp.id} className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400 font-medium">IF #{i + 1}:</span>
                      <span className="text-muted-foreground">
                        {comp.conditions.map((c, j) => {
                          const op = OPERATORS.find(o => o.value === c.operator)?.label || c.operator;
                          const needsVal = operatorNeedsValue(c.operator);
                          return `${c.variable || '?'} ${op}${needsVal ? ` "${c.value || '?'}"` : ''}`;
                        }).join(` ${comp.logicOperator === 'and' ? 'E' : 'OU'} `)}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">ELSE:</span>
                    <span className="text-muted-foreground">Se nenhum IF for verdadeiro</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="condition" />
    </>
  );
}
